import { zodResolver } from '@hookform/resolvers/zod';
import { trpc } from '@utils/trpc';
import { useForm } from 'react-hook-form';
import type z from 'zod';
import { ControlledBetterSelectInput, createSimpleOptions } from '~/components/form-fields/BetterSelectInput';
import { BusinessUserSelect } from '~/components/form-fields/custom/BusinessUserSelect';
import { ReferralSourceSelect } from '~/components/form-fields/custom/ReferralSourceSelect';
import { PhoneInput } from '~/components/form-fields/native-html/PhoneInput';
import { TextArea } from '~/components/form-fields/native-html/TextArea';
import { TextInput } from '~/components/form-fields/native-html/TextInput';
import { MutationButton } from '~/components/utils/buttons';
import { ModalDialog } from '~/components/utils/ModalDialog';
import { useUserContext } from '~/react_context/UserContext';
import type { LeadFull } from '~/server/schema/lead.schema';
import { hasContact, LeadSchema } from '~/server/schema/lead.schema';

type LeadFormInput = z.input<typeof LeadSchema.manualLeadSchema>;
type LeadFormData = z.output<typeof LeadSchema.manualLeadSchema>;

interface LeadFormModalProps {
	// When set the modal edits this lead; otherwise it creates a new one.
	lead?: LeadFull;
	onClose: () => void;
}

export const LeadFormModal = ({ lead, onClose }: LeadFormModalProps) => {
	const {
		id: currentUserId,
		visibility: {
			filteredOptions: { filteredJobCategories },
		},
	} = useUserContext();
	const getBusinessUserOpts = trpc.users.getBusinessUserOpts.useQuery();
	const { data: branches } = trpc.branch.readAll.useQuery();
	const branchOptions = (branches ?? []).map((branch) => ({ label: branch.name, value: branch.id }));
	const jobTypeOptions = createSimpleOptions(filteredJobCategories);
	const createLeadMutation = trpc.leads.createLead.useMutation();
	const updateLeadMutation = trpc.leads.updateLead.useMutation();
	const mutation = lead ? updateLeadMutation : createLeadMutation;

	const {
		control,
		formState: { errors },
		handleSubmit,
		register,
	} = useForm<LeadFormInput, unknown, LeadFormData>({
		defaultValues: {
			address: lead?.address ?? '',
			branchId: lead?.branchId ?? null,
			description: lead?.description ?? '',
			email: lead?.email ?? '',
			jobType: lead?.jobType ?? null,
			name: lead?.name ?? '',
			ownerId: lead ? lead.ownerId : currentUserId,
			phone: lead?.phone ?? '',
			referralSourceOptionId: lead?.referralSourceOptionId ?? null,
		},
		// Quick-estimate leads can arrive with no contact; editing one must not demand it first.
		resolver: zodResolver(lead && !hasContact(lead) ? LeadSchema.leadFieldsSchema : LeadSchema.manualLeadSchema),
	});

	const onSubmit = handleSubmit((values) => {
		if (lead) updateLeadMutation.mutate({ ...values, id: lead.id }, { onSuccess: onClose });
		else createLeadMutation.mutate(values, { onSuccess: onClose });
	});

	return (
		<ModalDialog closeFn={onClose} maxW="sm:max-w-lg">
			<form className="space-y-4" onSubmit={onSubmit}>
				<h3 className="text-lg font-medium text-gray-900">{lead ? 'Edit Lead' : 'New Lead'}</h3>

				<TextInput
					errorMessage={errors.name?.message}
					id="lead-name"
					labelText="Name"
					name="name"
					placeholder="Who is the lead?"
					register={register}
					required
				/>

				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<TextInput errorMessage={errors.email?.message} id="lead-email" labelText="Email" name="email" register={register} type="email" />
					<PhoneInput control={control} errorMessage={errors.phone?.message} id="lead-phone" labelText="Phone" name="phone" />
				</div>

				<TextInput errorMessage={errors.address?.message} id="lead-address" labelText="Address" name="address" register={register} />

				<TextArea
					errorMessage={errors.description?.message}
					id="lead-description"
					labelText="Description"
					name="description"
					register={register}
					rows={3}
				/>

				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<BusinessUserSelect
						canBeNull
						control={control}
						errors={errors}
						getBusinessUserOpts={getBusinessUserOpts}
						id="lead-owner"
						isFullWidth
						labelText="Lead Owner"
						name="ownerId"
						placeholder="Unassigned"
					/>
					<ReferralSourceSelect control={control} id="lead-referral-source" isFullWidth name="referralSourceOptionId" />
				</div>

				<div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
					<ControlledBetterSelectInput
						canBeNull
						control={control}
						id="lead-job-type"
						isFullWidth
						labelText="Job Type"
						name="jobType"
						options={jobTypeOptions}
						placeholder="Select a job type"
					/>
					{branchOptions.length > 0 && (
						<ControlledBetterSelectInput
							canBeNull
							control={control}
							id="lead-branch"
							isFullWidth
							labelText="Branch"
							name="branchId"
							options={branchOptions}
							placeholder="Select a branch"
						/>
					)}
				</div>

				{mutation.isError && <p className="text-sm text-red-600">{mutation.error.message}</p>}

				<div className="flex justify-end gap-3">
					<button
						className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-100"
						onClick={onClose}
						type="button"
					>
						Cancel
					</button>
					<MutationButton
						className="rounded-md bg-primary px-4 py-2 text-sm text-white transition hover:bg-primary-dark"
						id="save-lead"
						mutation={mutation}
						type="submit"
					>
						{lead ? 'Save Lead' : 'Create Lead'}
					</MutationButton>
				</div>
			</form>
		</ModalDialog>
	);
};
